import { useCallback, useEffect, useRef, useState } from 'react'
import { KeyRound, Ticket, Users } from 'lucide-react'
import { Link } from '../router.js'
import { adminMutate, apiRead, usePermissions } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { useSaveBar } from '../shell/editing.js'
import { useConfirm } from '../shell/confirm.js'
import {
  JETON_BILLETTERIE_RX, messageErreur, pastilleLiaison, phraseCopie, quandParis, validerAdresse, type ConnexionBilletterie,
} from './billetterie-model.js'

/**
 * ADMINISTRATION › INTÉGRATIONS › BILLETTERIE (LC Porte, 22/10/2026).
 *
 * Deux sens, deux secrets :
 *  · Gestion → billetterie (se porter garant d'une connexion LC Porte) : l'ADRESSE
 *    de la billetterie (une VALEUR → la Save Bar) et le JETON `bsk_` créé sur
 *    son serveur (un GESTE → « Tester » puis « Enregistrer », confirmation
 *    d'identité). Le jeton n'est jamais réaffiché.
 *  · billetterie → Gestion (copier les droits de porte chaque minute) : un
 *    ACCÈS DE SERVICE « billetterie.read », créé dans Accès de service. Ici,
 *    on dit seulement s'il existe et quand la billetterie a lu la liste.
 * Les 7 droits de porte se cochent dans Droits (Exploitation › Billetterie).
 */

type Lecture = { etat: 'chargement' } | { etat: 'ok'; data: ConnexionBilletterie } | { etat: 'erreur'; message: string }

export function BilletterieIntegrationPage() {
  const { permissions } = usePermissions()
  const peutModifier = permissions.includes('integration.write')
  const [l, setL] = useState<Lecture>({ etat: 'chargement' })
  const [adresse, setAdresse] = useState('')
  const adresseLue = useRef('')

  const appliquer = useCallback((c: ConnexionBilletterie) => {
    setL({ etat: 'ok', data: c })
    const lue = c.api_url ?? ''
    const precedente = adresseLue.current
    adresseLue.current = lue
    // Une relecture n'écrase pas une adresse en cours de saisie.
    setAdresse((brouillon) => (brouillon === precedente ? lue : brouillon))
  }, [])
  const charger = useCallback(() => {
    void apiRead<ConnexionBilletterie>('/api/v1/billetterie/connexion')
      .then((r) => { if (r.status === 200) appliquer(r.data); else setL({ etat: 'erreur', message: messageErreur(r.status, r.data, 'Le raccordement est illisible.') }) })
      .catch(() => setL({ etat: 'erreur', message: 'Gestion n’a pas répondu.' }))
  }, [appliquer])
  useEffect(() => { charger() }, [charger])

  const lue = l.etat === 'ok' ? l.data.api_url ?? '' : ''
  const propre = adresse.trim().replace(/\/+$/, '')
  const dirty = peutModifier && l.etat === 'ok' && propre !== lue
  useSaveBar({
    dirty,
    invalid: dirty ? validerAdresse(propre) : null,
    saveLabel: 'Enregistrer l’adresse',
    successMessage: 'Adresse de la billetterie enregistrée.',
    onSave: async () => {
      const r = await adminMutate<ConnexionBilletterie & { error?: string; message?: string }>('PUT', '/api/v1/billetterie/connexion', { api_url: propre })
      if (r.status === 200) { appliquer(r.data); setAdresse(r.data.api_url ?? ''); return true }
      if (r.data.error === 'STEPUP_REQUIRED') return 'Confirmation d’identité annulée : rien n’a été enregistré.'
      return messageErreur(r.status, r.data, 'L’adresse n’a pas été enregistrée.')
    },
    onCancel: () => setAdresse(lue),
  })

  const c = l.etat === 'ok' ? l.data : null
  const p = pastilleLiaison(c)
  const copie = c ? phraseCopie(c) : null

  return (
    <main className="lc-page lc-page--readable lc-stagger bi-page">
      <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
      <h1 className="lc-title">Billetterie (LC Porte)</h1>
      <p className="lc-subtitle">
        La billetterie du club tient la porte : billets, scans, caisses, téléphones LC Porte. Gestion s’en porte garant
        quand quelqu’un se connecte à LC Porte avec Gestion, et lui donne les droits de porte de chacun.
      </p>

      <section className="lc-panel bi-section" aria-labelledby="bi-raccord">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="bi-raccord"><Ticket size={16} aria-hidden="true" /> Connexion</h2>
          <span className={`lc-pill lc-pill--${p.ton}`}><span className={`lc-dot${p.ton === 'idle' ? '' : ` lc-dot--${p.ton}`}`} aria-hidden="true" />{p.texte}</span>
        </header>
        <div className="lc-panel__body bi-reglages">
          {l.etat === 'chargement' ? <p className="lc-hint" aria-busy="true">Lecture du raccordement…</p>
            : l.etat === 'erreur' ? <p className="bi-gris" role="status">{l.message}</p> : null}
          {c ? (
            <>
              <Field label="Adresse de la billetterie" helper="Sur le réseau du club. Le jeton voyage dans l’en-tête, jamais dans l’adresse.">
                <TextInput value={adresse} onChange={setAdresse} disabled={!peutModifier} placeholder="http://billetterie-club:4192" ariaLabel="Adresse de la billetterie" />
              </Field>
              {c.status.reachable
                ? <p className="cle-verdict cle-verdict--ok" role="status">La billetterie répond et reconnaît le jeton de Gestion.</p>
                : c.status.message ? <p className={`cle-verdict${c.status.error_code === 'BILLETTERIE_TOKEN_REFUSED' || c.status.error_code === 'BILLETTERIE_AUTRE_SERVICE' ? ' cle-verdict--ko' : ''}`} role="status">{c.status.message}</p> : null}
            </>
          ) : null}
        </div>
      </section>

      {c ? <JetonPanneau etat={c.token} peutModifier={peutModifier} onEnregistre={appliquer} /> : null}

      {c && copie ? (
        <section className="lc-panel bi-section" aria-labelledby="bi-copie">
          <header className="lc-panel__head">
            <h2 className="lc-section-title" id="bi-copie"><Users size={16} aria-hidden="true" /> Droits de porte</h2>
          </header>
          <div className="lc-panel__body bi-reglages">
            <p className={`cle-verdict${copie.ton === 'ok' ? ' cle-verdict--ok' : copie.ton === 'warning' ? ' cle-verdict--doute' : ''}`} role="status">{copie.texte}</p>
            <p className="lc-hint">
              Les 7 droits de porte (scanner, forcer l’entrée, vendre, ouvrir et fermer une caisse, exonérer, chef de porte) se cochent
              dans les <Link href="/settings/roles">modèles de droits</Link> (ou les droits d’une personne), module Exploitation › Billetterie. L’accès de la billetterie à cette
              liste se crée dans <Link href="/settings/integrations/service-credentials">Accès de service</Link> (périmètre « billetterie.read »).
            </p>
          </div>
        </section>
      ) : null}
    </main>
  )
}

function JetonPanneau({ etat, peutModifier, onEnregistre }: {
  etat: { set: boolean; updated_at: string | null }
  peutModifier: boolean
  onEnregistre: (c: ConnexionBilletterie) => void
}) {
  const confirm = useConfirm()
  // Toujours vide au départ : la valeur ne vient JAMAIS du serveur.
  const [jeton, setJeton] = useState('')
  const [essai, setEssai] = useState(false)
  const [envoi, setEnvoi] = useState(false)
  const [verdict, setVerdict] = useState<{ ok: boolean | null; texte: string; pour: string } | null>(null)
  const [apres, setApres] = useState<{ ok: boolean | null; texte: string } | null>(null)
  const valeur = jeton.trim()
  const forme = JETON_BILLETTERIE_RX.test(valeur)
  const courant = verdict && verdict.pour === valeur ? verdict : null

  const tester = async () => {
    setEssai(true); setApres(null)
    try {
      const r = await adminMutate<{ ok?: boolean; message?: string; error?: string }>('POST', '/api/v1/billetterie/jeton/test', { token: valeur })
      if (r.status === 200 && typeof r.data.ok === 'boolean') setVerdict({ ok: r.data.ok, texte: r.data.message ?? '', pour: valeur })
      else setVerdict({ ok: null, texte: messageErreur(r.status, r.data, 'L’essai n’a pas eu lieu.'), pour: valeur })
    } catch { setVerdict({ ok: null, texte: 'Gestion n’a pas répondu : l’essai n’a pas eu lieu.', pour: valeur }) } finally { setEssai(false) }
  }

  const enregistrer = async () => {
    if (courant?.ok !== true) {
      const ok = await confirm({
        title: 'Enregistrer un jeton qui n’a pas été reconnu ?',
        message: courant
          ? `Le dernier essai n’a pas abouti : ${courant.texte} S’il est faux, personne ne pourra plus se connecter à LC Porte avec Gestion (le QR restera possible).`
          : 'Ce jeton n’a pas été essayé. S’il est faux, personne ne pourra plus se connecter à LC Porte avec Gestion (le QR restera possible). Mieux vaut cliquer d’abord sur « Tester ».',
        variant: 'sensitive', confirmLabel: 'Enregistrer quand même',
      })
      if (!ok) return
    }
    setEnvoi(true); setApres(null)
    try {
      const r = await adminMutate<ConnexionBilletterie & { error?: string; message?: string }>('PUT', '/api/v1/billetterie/jeton', { token: valeur })
      if (r.status === 200) {
        // Vidé AVANT tout le reste : le secret ne séjourne pas dans la page.
        setJeton(''); setVerdict(null)
        setApres({ ok: true, texte: 'Jeton rangé au coffre de Gestion. Il ne sera jamais réaffiché.' })
        onEnregistre(r.data)
        return
      }
      setApres({ ok: r.data.error === 'STEPUP_REQUIRED' ? null : false, texte: r.data.error === 'STEPUP_REQUIRED' ? 'Confirmation d’identité annulée : rien n’a été enregistré.' : messageErreur(r.status, r.data, 'Le jeton n’a pas été enregistré.') })
    } catch { setApres({ ok: null, texte: 'Gestion n’a pas répondu : relisez la page avant de réessayer.' }) } finally { setEnvoi(false) }
  }

  const ton = (ok: boolean | null) => (ok === true ? ' cle-verdict--ok' : ok === false ? ' cle-verdict--ko' : '')
  return (
    <section className="lc-panel cle-panel bi-section" aria-labelledby="bi-jeton">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="bi-jeton"><KeyRound size={16} aria-hidden="true" /> Jeton de Gestion</h2>
        <span className={`lc-pill ${etat.set ? 'lc-pill--ok' : 'lc-pill--idle'}`}><span className={`lc-dot${etat.set ? ' lc-dot--ok' : ''}`} aria-hidden="true" />{etat.set ? 'Enregistré' : 'Absent'}</span>
      </header>
      <div className="lc-panel__body cle-corps">
        <dl className="cle-faits">
          <div>
            <dt>Jeton actuel</dt>
            <dd>{etat.set ? `Enregistré ${quandParis(etat.updated_at)}` : 'Aucun jeton : LC Porte ne peut pas encore se connecter avec Gestion.'}</dd>
          </div>
          <div>
            <dt>Où le créer</dt>
            <dd>Sur le serveur de la billetterie : « jeton-service gestion ». L’ancien jeton y reste valable une heure, le temps de le remplacer ici.</dd>
          </div>
        </dl>
        {peutModifier ? (
          <div className="cle-saisie">
            <label className="cle-champ">
              <span>Nouveau jeton</span>
              <input className="lc-input" type="password" autoComplete="new-password" spellCheck={false} name="billetterie-jeton"
                value={jeton} aria-label="Nouveau jeton de la billetterie"
                onChange={(e) => { setJeton(e.target.value); setApres(null) }} />
            </label>
            <div className="cle-actions">
              <button type="button" className="lc-btn" disabled={!forme || essai || envoi} onClick={() => { void tester() }}>{essai ? 'Essai en cours…' : 'Tester'}</button>
              <button type="button" className="lc-btn lc-btn--primary" disabled={!forme || essai || envoi} onClick={() => { void enregistrer() }}>{envoi ? 'Enregistrement…' : 'Enregistrer le jeton'}</button>
            </div>
            {valeur && !forme ? <p className="lc-hint">Un jeton de la billetterie commence par « bsk_ » et compte 47 caractères.</p> : null}
            {courant ? <p role="status" className={`cle-verdict${ton(courant.ok)}`}>{courant.texte}</p> : null}
          </div>
        ) : <p className="lc-hint">Seul un compte autorisé à modifier les intégrations peut remplacer ce jeton.</p>}
        {apres ? <p role="status" className={`cle-verdict${ton(apres.ok)}`}>{apres.texte}</p> : null}
      </div>
    </section>
  )
}
